import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { withFloorDefault } from "@/components/UsedVanDialog";
import { REPRISE_CHOICES } from "@/components/RepriseBrand";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const TYPES = { van: "van", fourgon: "fourgon" };
export const TYPE_LABELS = [["van", "Van chevaux"], ["fourgon", "Remorque fourgon"], ["autre", "Autre"]];
export const PLACES = REPRISE_CHOICES.find(([k]) => k === "places")[2];
export const TRADEIN_STATUSES = ["Encore chez le client", "Reprise effectuée"];
export const tradeInStatus = (t) => t.status || TRADEIN_STATUSES[0];

export const tradeInToUsed = (t, source) => withFloorDefault({
  ...(source ? { source_tradein: source } : {}),
  type: TYPES[t.type] || "autre", brand: t.marque || "", brand_other: !!t.marqueAutre, model: t.modele || "",
  places: t.type === "van" ? (t.places || "") : "", year: t.annee || "", plate: t.immat || "", ptac: t.ptac || "",
  floor_type: t.plancher_type || "", vat_recoverable: !!t.tva_recuperable, photos: t.photos || [],
  cost_ht: t.prix_reprise_ht ? String(Math.round(t.prix_reprise_ht * 100) / 100) : "",
});

export const UnsignedBadge = ({ testid, label = "Devis non signé" }) => (
  <span className="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300" data-testid={testid}>{label}</span>
);

export const dateFR = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");

export const useTradeIns = (active = true, includeHidden = false) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/trade-ins").then(({ data }) => setRows(data.flatMap((x) => (x.trade_ins || [])
    .map((t, i) => ({ key: `${x.id}-${i}`, quoteId: x.id, index: i, t, quote: x.quote_number, client: [x.client?.name, x.client?.firstname].filter(Boolean).join(" "), date: x.created_at, seller: x.reseller_name, place: x.delivery_place || "", signed: x.signed !== false }))
    .filter((r) => (includeHidden || !r.t.hidden_from_list) && (r.t.marque || r.t.modele || "").trim())))).catch(() => setRows([]));
  useEffect(() => { if (active) { setRows(null); load(); } }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return [rows, setRows, load];
};

export const TradeInFilters = ({ q, setQ, type, setType, places, setPlaces, children }) => (
  <div className="flex flex-wrap gap-2">
    <Input placeholder="Rechercher (client, n° de devis, marque, immat…)" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 min-w-[200px] basis-full sm:basis-auto" data-testid="tradein-search" />
    <Select value={type} onValueChange={(v) => { setType(v); if (v !== "van" && v !== "all") setPlaces("all"); }}>
      <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-44" data-testid="tradein-filter-type"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">Toutes gammes</SelectItem>{TYPE_LABELS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
    <Select value={places} onValueChange={setPlaces} disabled={type !== "all" && type !== "van"}>
      <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-44" data-testid="tradein-filter-places"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">Toutes places</SelectItem>{PLACES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
    </Select>
    {children}
  </div>
);

export const filterTradeIns = (rows, { q, type, places, status, seller }) => {
  const s = q.trim().toLowerCase();
  return (rows || []).filter((r) => (!s || [r.quote, r.client, r.seller, r.t.marque, r.t.modele, r.t.immat].join(" ").toLowerCase().includes(s))
    && (type === "all" || (TYPES[r.t.type] || "autre") === type)
    && (places === "all" || r.t.places === places)
    && (!status || status === "all" || tradeInStatus(r.t) === status)
    && (!seller || seller === "all" || r.seller === seller));
};

export const ImportTradeInDialog = ({ open, onOpenChange, onPick }) => {
  const [rows] = useTradeIns(open, true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [places, setPlaces] = useState("all");
  const [status, setStatus] = useState("all");
  useEffect(() => { if (open) { setQ(""); setType("all"); setPlaces("all"); setStatus("all"); } }, [open]);
  const list = useMemo(() => filterTradeIns(rows, { q, type, places, status }), [rows, q, type, places, status]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="import-tradein-dialog">
        <DialogHeader>
          <DialogTitle>Importer une reprise</DialogTitle>
          <DialogDescription>Choisissez un véhicule repris sur un devis, signé ou non : ses informations remplissent la fiche occasion.</DialogDescription>
        </DialogHeader>
        <TradeInFilters q={q} setQ={setQ} type={type} setType={setType} places={places} setPlaces={setPlaces}>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="import-tradein-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{TRADEIN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </TradeInFilters>
        {!rows ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto my-6" /> : !list.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="import-tradein-empty">Aucune reprise trouvée.</p>
        ) : (
          <div className="divide-y divide-border min-w-0">
            {list.map((r) => (
              <div key={r.key} className="flex items-center gap-3 py-2.5" data-testid={`import-tradein-row-${r.key}`}>
                {r.t.photos?.[0]?.url ? <img src={r.t.photos[0].url} alt="" className="w-14 h-11 object-cover rounded border border-border" /> : <div className="w-14 h-11 rounded bg-muted" />}
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium truncate">{[r.t.marque, r.t.modele].filter(Boolean).join(" ")}{r.t.places ? ` · ${r.t.places}` : ""}{r.t.annee ? ` · ${r.t.annee}` : ""}{r.t.immat ? ` · ${r.t.immat}` : ""}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.quote} · {r.client || "—"} · {dateFR(r.date)}{r.seller ? ` · ${r.seller}` : ""} · {tradeInStatus(r.t)}</div>
                  {!r.signed && <UnsignedBadge testid={`import-tradein-unsigned-${r.key}`} />}
                </div>
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => { onPick(tradeInToUsed(r.t, { quote_id: r.quoteId, index: r.index })); onOpenChange(false); }} data-testid={`import-tradein-pick-${r.key}`}>Importer</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
